export interface AuthStatus {
  registered: boolean;
  authed: boolean;
}

export interface RealtimeTokenResponse {
  token: { value?: string; client_secret?: { value: string }; [k: string]: unknown };
  iceServers: RTCIceServer[];
  model: string;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return (await res.json()) as T;
}

export const api = {
  authStatus: () => fetch("/api/auth/status").then((r) => json<AuthStatus>(r)),

  registerOptions: (code?: string) =>
    fetch("/api/auth/register/options", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code }),
    }).then((r) => json<PublicKeyCredentialCreationOptionsJSON>(r)),

  enrollStart: () =>
    fetch("/api/auth/enroll/start", { method: "POST" }).then((r) =>
      json<{ code: string; expiresInMin: number }>(r),
    ),
  registerVerify: (body: unknown) =>
    fetch("/api/auth/register/verify", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => json<{ verified: boolean }>(r)),

  loginOptions: () =>
    fetch("/api/auth/login/options", { method: "POST" }).then((r) => json<PublicKeyCredentialRequestOptionsJSON>(r)),
  loginVerify: (body: unknown) =>
    fetch("/api/auth/login/verify", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => json<{ verified: boolean }>(r)),

  logout: () => fetch("/api/auth/logout", { method: "POST" }),

  getSettings: () =>
    fetch("/api/settings").then((r) => json<{ settings: { voice?: string }; voices: string[] }>(r)),
  patchSettings: (patch: { voice?: string }) =>
    fetch("/api/settings", {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    }).then((r) => json<{ voice?: string }>(r)),

  listMcp: () => fetch("/api/mcp/servers").then((r) => json<McpListResponse>(r)),
  addMcp: (body: McpAddBody) =>
    fetch("/api/mcp/servers", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => json<{ ok: boolean; server: McpServerInfo }>(r)),
  removeMcp: (name: string) =>
    fetch(`/api/mcp/servers/${encodeURIComponent(name)}`, { method: "DELETE" }).then((r) =>
      json<{ ok: boolean }>(r),
    ),
  authorizeMcp: (server: string, redirectUrl?: string) =>
    fetch("/api/mcp/authorize", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ server, redirectUrl }),
    }).then((r) => json<{ ok: boolean; summary: string }>(r)),

  realtimeToken: (conversationId?: string) =>
    fetch("/api/realtime/token", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ conversationId }),
    }).then((r) => json<RealtimeTokenResponse>(r)),

  callTool: (
    tool: string,
    args: Record<string, unknown>,
    callId?: string,
    conversationId?: string | null,
  ) =>
    fetch("/api/tool", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ tool, args, callId, conversationId: conversationId ?? undefined }),
    }).then((r) => json<{ callId: string; ok: boolean; summary: string; detail?: unknown }>(r)),
};

// --- MCP types -------------------------------------------------------------
export interface McpServerInfo {
  name: string;
  transport: "stdio" | "http";
  auth: "none" | "bearer" | "oauth";
  lifecycle: "lazy" | "eager" | "keep-alive";
  command?: string;
  args?: string[];
  url?: string;
  envVars: string[];
  missingSecrets: string[];
  ready: boolean;
}
export interface McpCatalogEntry {
  id: string;
  title: string;
  description: string;
  transport: "stdio" | "http";
  secret?: { env: string; label: string };
  def: Record<string, unknown>;
}
export interface McpListResponse {
  servers: McpServerInfo[];
  catalog: McpCatalogEntry[];
}
export interface McpAddBody {
  catalogId?: string;
  token?: string;
  name?: string;
  def?: Record<string, unknown>;
  secrets?: Record<string, string>;
}

// Minimal JSON option types (from @simplewebauthn/browser at runtime).
export type PublicKeyCredentialCreationOptionsJSON = Record<string, unknown>;
export type PublicKeyCredentialRequestOptionsJSON = Record<string, unknown>;
